import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isObject } from 'util/objects'
import { selectQueryParams } from 'selectors/location'
import { selectPreviousDrawer } from 'ducks/drawers/selectors'
import useComponentId from 'util/hooks/useComponentId'
import Drawers from 'ducks/drawers/components'
import { doOpenDrawer, doCloseDrawer, doAddDrawerParam } from './actions'
import {
  extractDrawerPropsById,
  stripDrawerIdsFromProps,
  getCurrentDrawerId,
  isResourceId,
} from './util'

const NullComponent = () => <></>
const drawerConfig = {}

export function useDrawer(config) {
  const { id, type, closeIgnoresStack = false } = isObject(config)
    ? config
    : { type: config }
  const generatedDrawerId = useComponentId()
  const drawerId = id || generatedDrawerId
  const dispatch = useDispatch()
  const queryParams = useSelector(selectQueryParams)
  const previousDrawer = useSelector(selectPreviousDrawer)

  const rawDrawerProps = extractDrawerPropsById(drawerId, queryParams)

  const {
    drawer,
    drawerResourceId,
    ...restSanitizedDrawerProps
  } = stripDrawerIdsFromProps(rawDrawerProps)

  const openDrawer = useCallback(
    (inputResourceId, options) => {
      const resourceId = isResourceId(inputResourceId) ? inputResourceId : null
      drawerConfig[drawerId] = {
        id,
        type,
        closeIgnoresStack,
        additionalProps: (options && options.additionalProps) || {},
      }

      dispatch(doOpenDrawer(drawerId, type, resourceId, options))
    },
    [id, dispatch, type, drawerId, closeIgnoresStack]
  )

  const closeDrawer = useCallback(
    options => {
      dispatch(doCloseDrawer({ ignoreStack: closeIgnoresStack, ...options }))
      delete drawerConfig[drawerId]
    },
    [dispatch, closeIgnoresStack, drawerId]
  )

  const updateDrawerParameter = useCallback(
    (paramKey, paramValue, options) => {
      dispatch(doAddDrawerParam(drawerId, paramKey, paramValue, options))
    },
    [dispatch, drawerId]
  )

  return {
    openDrawer,
    closeDrawer,
    updateDrawerParameter,
    drawerResourceId,
    drawerId,
    previousDrawer,
    ...restSanitizedDrawerProps,
  }
}

export function useRenderDrawers() {
  const dispatch = useDispatch()
  const queryParams = useSelector(selectQueryParams)
  const drawerId = getCurrentDrawerId(queryParams)
  const previousDrawer = useSelector(selectPreviousDrawer)
  const rawDrawerProps = extractDrawerPropsById(drawerId, queryParams)

  const {
    drawer: drawerType,
    drawerResourceId,
    ...restSanitizedDrawerProps
  } = stripDrawerIdsFromProps(rawDrawerProps)

  const Drawer = drawerType ? Drawers[drawerType] : NullComponent

  const { closeIgnoresStack = false, additionalProps = {} } =
    drawerConfig[drawerId] || {}

  const closeDrawer = useCallback(
    options => {
      dispatch(doCloseDrawer({ ignoreStack: closeIgnoresStack, ...options }))
      delete drawerConfig[drawerId]
    },
    [dispatch, closeIgnoresStack, drawerId]
  )

  const closeDrawerIgnoreStack = useCallback(
    options => {
      closeDrawer({ ...options, ignoreStack: true })
    },
    [closeDrawer]
  )

  const updateDrawerParameter = useCallback(
    (paramKey, paramValue, options) => {
      dispatch(doAddDrawerParam(drawerId, paramKey, paramValue, options))
    },
    [dispatch, drawerId]
  )

  const drawerProps = {
    drawer: drawerType,
    drawerResourceId,
    ...restSanitizedDrawerProps,
    ...additionalProps,
    open: true,
    onClose: closeDrawer,
    onExit: closeDrawerIgnoreStack,
    updateDrawerParameter,
    key: `${drawerType}-${drawerId}`,
    drawerId,
    previousDrawer,
  }

  return {
    // eslint-disable-next-line react/jsx-filename-extension
    Drawer,
    drawerProps,
  }
}
